const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';
const SCROLL_ZOOM_SCALE_AMOUNT = 0.2 / 100;
const scrollZoomAnimationState = {
  elements: new Set(),
  visibleElements: new Set(),
  observer: null,
  rafId: 0,
  listenersBound: false,
};

// Scroll in animation logic
function onIntersection(elements, observer) {
  for (const [index, element] of elements.entries()) {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  }
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(
    rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME),
  );
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    for (const element of animationTriggerElements) {
      element.classList.add('scroll-trigger--design-mode');
    }
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  for (const element of animationTriggerElements) {
    observer.observe(element);
  }
}

function cleanupDetachedScrollZoomTargets() {
  if (
    scrollZoomAnimationState.elements.size === 0 &&
    scrollZoomAnimationState.visibleElements.size === 0
  ) {
    return;
  }

  /* 再描画で DOM から外れた要素を監視対象から除去し、古いカード参照を残さない */
  const detachedElements = new Set();

  for (const element of scrollZoomAnimationState.elements) {
    if (!element.isConnected) detachedElements.add(element);
  }

  for (const element of scrollZoomAnimationState.visibleElements) {
    if (!element.isConnected) detachedElements.add(element);
  }

  for (const element of detachedElements) {
    scrollZoomAnimationState.observer?.unobserve(element);
    scrollZoomAnimationState.elements.delete(element);
    scrollZoomAnimationState.visibleElements.delete(element);
  }
}

function queueScrollZoomAnimationFrame() {
  if (scrollZoomAnimationState.rafId !== 0) return;

  scrollZoomAnimationState.rafId = window.requestAnimationFrame(() => {
    scrollZoomAnimationState.rafId = 0;
    cleanupDetachedScrollZoomTargets();

    const visibleElements = Array.from(scrollZoomAnimationState.visibleElements);
    if (visibleElements.length === 0) return;

    /*
     * getBoundingClientRect の読み取りを先にまとめ、style 書き込みは後段で一括実行する。
     * read と write を交互に行うと、scroll 中にブラウザが強制リフローを繰り返し発生させ重くなるため。
     */
    const zoomRatios = visibleElements.map(
      (element) => 1 + SCROLL_ZOOM_SCALE_AMOUNT * percentageSeen(element),
    );

    for (const [index, element] of visibleElements.entries()) {
      element.style.setProperty('--zoom-in-ratio', zoomRatios[index]);
    }
  });
}

function bindScrollZoomAnimationListeners() {
  if (scrollZoomAnimationState.listenersBound) return;

  window.addEventListener('scroll', queueScrollZoomAnimationFrame, { passive: true });
  window.addEventListener('resize', queueScrollZoomAnimationFrame);
  scrollZoomAnimationState.listenersBound = true;
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger(rootEl = document) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  cleanupDetachedScrollZoomTargets();

  const animationTriggerElements = Array.from(
    rootEl.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME),
  );

  if (animationTriggerElements.length === 0) return;

  if (!scrollZoomAnimationState.observer) {
    scrollZoomAnimationState.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          scrollZoomAnimationState.visibleElements.add(entry.target);
        } else {
          scrollZoomAnimationState.visibleElements.delete(entry.target);
        }
      }

      queueScrollZoomAnimationFrame();
    });
  }

  bindScrollZoomAnimationListeners();

  const newElements = animationTriggerElements.filter(
    (element) => !scrollZoomAnimationState.elements.has(element),
  );

  for (const element of newElements) {
    scrollZoomAnimationState.elements.add(element);
    scrollZoomAnimationState.observer.observe(element);
  }

  /* 初回描画直後の見え方も既存どおりに合わせる */
  const initialZoomRatios = animationTriggerElements.map(
    (element) => 1 + SCROLL_ZOOM_SCALE_AMOUNT * percentageSeen(element),
  );
  for (const [index, element] of animationTriggerElements.entries()) {
    element.style.setProperty('--zoom-in-ratio', initialZoomRatios[index]);
  }
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  const percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) =>
    initializeScrollAnimationTrigger(event.target, true),
  );
  document.addEventListener('shopify:section:reorder', () =>
    initializeScrollAnimationTrigger(document, true),
  );
}
